import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useRealtimeTags } from 'Core/hooks';
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTagsManage } from 'Modules/.common/components/TagsManage/useTagsManage';
import TagTreeFilter from '../../../../../customControls/TagTreeFilter';

import { TABLE_FILTER_CONFIG, TABLE_FILTER_TYPES } from '../../constants/tableFilterConfig';
import { resetSearchResults, searchTableFilterRecords } from '../../../redux/slice';
import './index.scss';

const TAG_ID_ALL_TAGS = -99;

const TableFilterSearch = ({
  onResultSelect = undefined,
  selectedIdsByType = undefined,
  onSearchValueChange = undefined,
  searchInputRef = undefined,
}) => {
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const searchState = useSelector((state) => state.inspectionHistoryRedux?.search);
  const catalogTags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] })
  const user = useSelector((state) => state.securityRedux?.user);
  const [searchValue, setSearchValue] = useState('');
  const [activeTab, setActiveTab] = useState(TABLE_FILTER_TYPES[0]);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const activeRequestRef = useRef(null);

  // Get tag data from useTagsManage hook for TagTreeFilter
  const { tags: managedTags, isLoading: tagsLoading } = useTagsManage({ onlyTags: true });

  // Get current tab configuration
  const currentTabConfig = TABLE_FILTER_CONFIG[activeTab];
  const isLocalFilterTab = currentTabConfig?.useLocalFilter;

  // Get user's accessible tags (filtered by permission)
  const accessibleTags = useMemo(() => {
    if (!catalogTags?.length) {
      return [];
    }

    // Parse user's tag IDs
    const userTagIds = user?.tagIds ? JSON.parse(user.tagIds) : [];
    const hasAllTagsPermission = userTagIds.includes(TAG_ID_ALL_TAGS);

    // Filter out the "all tags" special tag
    const filteredTags = catalogTags.filter((tag) => tag?.id !== TAG_ID_ALL_TAGS);

    // If user has all tags permission, return all tags
    if (hasAllTagsPermission) {
      return filteredTags;
    }

    // Get tags that are not disabled (user has direct access)
    const enabledTags = filteredTags.filter((tag) => tag?.disabled === false);

    // Also include parent tags based on tagPath to show hierarchy
    const tagIdsToShow = enabledTags
      .map((tag) => tag?.tagPath?.split('/'))
      .flat()
      .filter(Boolean);
    const uniqueTagIds = [...new Set(tagIdsToShow)];

    return filteredTags.filter((tag) => uniqueTagIds.includes(tag.id?.toString()));
  }, [catalogTags, user?.tagIds]);

  // Filter tags locally based on search value
  const filteredLocalTags = useMemo(() => {
    if (!isLocalFilterTab || activeTab !== 'tags') {
      return [];
    }

    const trimmedSearch = searchValue.trim().toLowerCase();
    return accessibleTags
      .filter((tag) => tag && tag.id && tag.label)
      .filter((tag) => {
        if (!trimmedSearch) {
          return true;
        }
        return tag.label.toLowerCase().includes(trimmedSearch);
      })
      .map((tag) => ({
        id: String(tag.id),
        label: tag.label,
        color: tag.color,
        tagPath: tag.tagPath,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [accessibleTags, searchValue, isLocalFilterTab, activeTab]);

  // Expose clearSearch method via ref
  React.useImperativeHandle(searchInputRef, () => ({
    clearSearch: () => {
      setSearchValue('');
      setIsOpen(false);
      dispatch(resetSearchResults({ types: [activeTab] }));
    },
  }), [activeTab, dispatch]);

  // Notify parent when search value changes
  useEffect(() => {
    onSearchValueChange?.(searchValue);
  }, [searchValue, onSearchValueChange]);

  const trimmedQuery = searchValue.trim();
  const tabOptions = useMemo(() => TABLE_FILTER_TYPES.map((type) => ({
    id: type,
    label: messages[TABLE_FILTER_CONFIG[type].groupLabelKey] || TABLE_FILTER_CONFIG[type].fallbackGroupLabel,
  })), [messages]);

  const selectedSetForTab = selectedIdsByType?.[activeTab];
  const currentTabState = searchState?.[activeTab] || {};
  const currentResults = isLocalFilterTab ? filteredLocalTags : (currentTabState.data || []);
  const tabLoading = isLocalFilterTab ? false : Boolean(currentTabState.loading);
  const tabErrorMessage = isLocalFilterTab ? '' : (currentTabState.error || '');

  useEffect(() => () => {
    if (activeRequestRef.current?.abort) {
      activeRequestRef.current.abort();
    }
  }, []);

  useEffect(() => {
    // Skip API calls for local filter tabs (like tags)
    if (isLocalFilterTab) {
      return;
    }

    if (!isOpen) {
      if (activeRequestRef.current?.abort) {
        activeRequestRef.current.abort();
        activeRequestRef.current = null;
      }
      dispatch(resetSearchResults({ types: [activeTab] }));
      return;
    }

    const handler = setTimeout(() => {
      if (activeRequestRef.current?.abort) {
        activeRequestRef.current.abort();
      }

      const promise = dispatch(searchTableFilterRecords({
        type: activeTab,
        pattern: trimmedQuery,
      }));

      activeRequestRef.current = promise;
      promise.finally(() => {
        if (activeRequestRef.current === promise) {
          activeRequestRef.current = null;
        }
      });
    }, 300);

    return () => {
      clearTimeout(handler);
      if (activeRequestRef.current?.abort) {
        activeRequestRef.current.abort();
        activeRequestRef.current = null;
      }
    };
  }, [activeTab, dispatch, isOpen, trimmedQuery, isLocalFilterTab]);

  const handleResultClick = (record) => {
    if (!record) {
      return;
    }

    const config = TABLE_FILTER_CONFIG[activeTab];
    const identifier = config.getId(record);
    if (!identifier || selectedSetForTab?.has(identifier)) {
      return;
    }

    onResultSelect?.(activeTab, record);
    setIsOpen(false);
    setSearchValue('');
    dispatch(resetSearchResults({ types: [activeTab] }));
  };

  // Handler for tag selection from TagTreeFilter
  const handleTagSelect = useCallback((tag) => {
    if (!tag) return;

    onResultSelect?.('tags', {
      id: tag.id,
      label: tag.label,
    });
    setIsOpen(false);
    setSearchValue('');
  }, [onResultSelect]);

  const handleClickAway = (event) => {
    if (containerRef.current && containerRef.current.contains(event.target)) {
      return;
    }
    setIsOpen(false);
    setSearchValue('');
    dispatch(resetSearchResults({ types: [activeTab] }));
  };

  const renderStatus = () => {
    if (tabLoading) {
      return (
        <div className='result-status'>
          <CircularProgress size={20} />
        </div>
      );
    }

    if (tabErrorMessage) {
      return (
        <Typography variant='body2' color='error' className='result-status'>
          {tabErrorMessage}
        </Typography>
      );
    }

    // For local filter tabs, show no results only when there are no items
    if (isLocalFilterTab) {
      if (currentResults.length === 0) {
        return (
          <Typography variant='body2' className='result-status'>
            {messages['evirPx_No_Results_Found']}
          </Typography>
        );
      }
      return null;
    }

    // For API-based tabs, require search input to show results
    if (currentResults.length === 0 && searchValue.trim().length > 0) {
      return (
        <Typography variant='body2' className='result-status'>
          {messages['evirPx_No_Results_Found']}
        </Typography>
      );
    }

    return null;
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div className='table-filter-search' ref={containerRef}>
        <TextField
          inputRef={inputRef}
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          onFocus={() => setIsOpen(true)}
          placeholder={messages['evirPx_Search_To_Filter'] || messages['evirPx_Search'] || messages.search || 'Search'}
          size='small'
          fullWidth
          className='search-field'
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <Search fontSize='small' />
              </InputAdornment>
            ),
          }}
        />
        {isOpen && (
          <Paper elevation={4} className='search-popover'>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              variant='fullWidth'
              className='search-tabs'
            >
              {tabOptions.map((tab) => (
                <Tab key={tab.id} value={tab.id} label={tab.label} />
              ))}
            </Tabs>
            <Box className={`tab-panel${activeTab === 'tags' ? ' tags-tree-panel' : ''}`}>
              {activeTab === 'tags' ? (
                // Show hierarchical tag tree for Tags tab using custom TagTreeFilter
                <Box sx={{ p: 1 }}>
                  {tagsLoading ? (
                    <div className='result-status'>
                      <CircularProgress size={20} />
                    </div>
                  ) : (
                    <TagTreeFilter
                      tags={managedTags || []}
                      searchText={searchValue}
                      onTagSelect={handleTagSelect}
                      selectedTagIds={selectedIdsByType?.tags}
                      height={280}
                    />
                  )}
                </Box>
              ) : (
                renderStatus() || (
                  <List className='result-list'>
                    {currentResults.map((record) => {
                      const config = TABLE_FILTER_CONFIG[activeTab];
                      const identifier = config.getId(record);
                      if (!identifier) {
                        return null;
                      }
                      const disabled = selectedSetForTab?.has(identifier);
                      const primary = config.getPrimaryLabel(record);

                      return (
                        <ListItemButton
                          key={`${activeTab}-${identifier}`}
                          disabled={disabled}
                          onClick={() => handleResultClick(record)}
                          className='result-item'
                        >
                          <ListItemText
                            primary={primary}
                            primaryTypographyProps={{ noWrap: true }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )
              )}
            </Box>
          </Paper>
        )}
      </div>
    </ClickAwayListener>
  );
};

TableFilterSearch.propTypes = {
  onResultSelect: PropTypes.func,
  selectedIdsByType: PropTypes.objectOf(
    PropTypes.instanceOf(Set),
  ),
  onSearchValueChange: PropTypes.func,
  searchInputRef: PropTypes.shape({ current: PropTypes.any }),
};

export default TableFilterSearch;
